import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  IconButton,
  Badge,
  useColorModeValue,
  Icon,
  Spinner,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FiFileText, FiTrash2, FiPlay, FiZap } from 'react-icons/fi';
import { useCampaignContext } from './CampaignContext';
import NodeDataPanel from './NodeDataPanel';
import { ICON_MAP, COLOR_MAP, getSummary } from '../../../../components/CRM/Campaigns/campaignConstants';

const MotionFlex = motion(Flex);

export default function NodeCard({ node, index, onClick }) {
  const {
    removeNode, testNode, executeNode, testResults, testRunning, testContact,
    agentTouchedIds, agentBuildStamp,
  } = useCampaignContext();
  const bg = useColorModeValue('white', 'gray.700');
  const border = useColorModeValue('gray.200', 'gray.600');
  const agentGlow = useColorModeValue('rgba(159, 122, 234, 0.45)', 'rgba(183, 148, 244, 0.5)');
  const color = COLOR_MAP[node.subType] || 'gray';
  const NodeIcon = ICON_MAP[node.subType] || FiFileText;
  const isBranching = node.subType === 'IF_ELSE' || node.subType === 'SWITCH' || node.subType === 'AB_SPLIT' || node.subType === 'WAIT_FOR';

  // Staggered entrance when FutureBot just replaced the canvas (build_campaign);
  // computed once at mount so later re-renders don't re-animate.
  const [entranceDelay] = useState(() => {
    const isAgentBuild = agentBuildStamp && Date.now() - agentBuildStamp < 4000;
    return isAgentBuild ? Math.min(index * 0.1, 1.6) : 0;
  });
  const isAgentTouched = !!agentTouchedIds?.has?.(node.id);

  const result = testResults?.[node.id];
  const isRunning = testRunning === node.id;
  const hasResult = !!result;

  const testStatusColor = result?.status === 'error' ? 'red'
    : result?.status === 'blocked' ? 'orange'
    : result?.status === 'soft_failed' ? 'orange'
    : result?.status === 'would_skip' ? 'yellow'
    : hasResult ? 'green' : null;

  return (
    <MotionFlex
      direction="column"
      align="center"
      w="100%"
      initial={{ opacity: 0, y: 12, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.3, delay: entranceDelay, ease: 'easeOut' }}
    >
      <Box
        bg={bg}
        border="1px solid"
        borderColor={isAgentTouched ? 'purple.400' : (hasResult ? `${testStatusColor}.300` : border)}
        borderLeft={isBranching ? '4px solid' : '1px solid'}
        borderLeftColor={isBranching ? `${color}.400` : (isAgentTouched ? 'purple.400' : (hasResult ? `${testStatusColor}.300` : border))}
        borderRadius="lg"
        p={4}
        w="100%"
        maxW="480px"
        cursor="pointer"
        onClick={() => onClick?.(node, index)}
        _hover={{ shadow: 'md', borderColor: `${color}.300` }}
        transition="all 0.3s"
        position="relative"
        {...(isAgentTouched
          ? { boxShadow: `0 0 0 3px ${agentGlow}` }
          : hasResult && {
              boxShadow: `0 0 0 1px var(--chakra-colors-${testStatusColor}-200)`,
            })}
      >
        <Flex align="center" gap={3}>
          <Flex
            align="center"
            justify="center"
            w={10}
            h={10}
            borderRadius="lg"
            bg={`${color}.50`}
            _dark={{ bg: `${color}.900` }}
          >
            <Icon as={NodeIcon} boxSize={5} color={`${color}.500`} />
          </Flex>

          <Box flex={1} minW={0}>
            <Flex align="center" gap={2} mb={0.5}>
              <Badge colorScheme={color} fontSize="xs" textTransform="uppercase">
                {node.subType.replace(/_/g, ' ')}
              </Badge>
              {hasResult && (
                <Box w={2} h={2} borderRadius="full" bg={`${testStatusColor}.400`} />
              )}
            </Flex>
            <Text fontSize="sm" color="gray.500" noOfLines={1}>
              {getSummary(node)}
            </Text>
          </Box>

          <Flex gap={1}>
            {testContact && (
              <>
                <IconButton
                  icon={isRunning ? <Spinner size="xs" /> : <FiPlay />}
                  size="sm"
                  variant="ghost"
                  colorScheme="blue"
                  aria-label="Test node (dry run)"
                  title="Test (dry run)"
                  isDisabled={isRunning}
                  onClick={(e) => {
                    e.stopPropagation();
                    testNode(node.id);
                  }}
                />
                <IconButton
                  icon={isRunning ? <Spinner size="xs" /> : <FiZap />}
                  size="sm"
                  variant="ghost"
                  colorScheme="green"
                  aria-label="Execute node (real)"
                  title="Execute (real)"
                  isDisabled={isRunning}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (['SEND_EMAIL', 'SEND_SMS'].includes(node.subType)) {
                      if (!window.confirm(`This will send a real ${node.subType === 'SEND_EMAIL' ? 'email' : 'SMS'} to the test contact. Continue?`)) return;
                    }
                    executeNode(node.id);
                  }}
                />
              </>
            )}
            <IconButton
              icon={<FiTrash2 />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              aria-label="Remove node"
              onClick={(e) => {
                e.stopPropagation();
                removeNode(node.id);
              }}
            />
          </Flex>
        </Flex>
      </Box>

      {/* Data panel appears below tested nodes */}
      <NodeDataPanel nodeId={node.id} subType={node.subType} />
    </MotionFlex>
  );
}
